import axios, { AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import handleResponseError from '../../lib/handleResponseError'
import { invalidateBrowserAuthState, isAuthEndpoint, isPublicAuthException } from '@/utils/authState'
import { DEFAULT_TIMEOUT } from '@/config/api-timeouts'

// Runtime environment detection based on hostname
const isStaging = window.location.hostname.includes('staging')
const isProduction = window.location.hostname.includes('commzone.app') && !isStaging

// Use environment-specific API URLs (matches NextJS configuration)
const NODE_BASE_URL = isStaging
    ? 'https://stagingapp.commzone.app/api'
    : isProduction
      ? 'https://app.commzone.app/api'
      : // In local dev, prefer the Vite proxy (`/api`) so port/host is centralized in `vite.config.ts`.
        // Override explicitly with VITE_LOCAL_BASE_URL only when you truly need to bypass the proxy.
        import.meta.env.VITE_LOCAL_BASE_URL || '/api'

const PYTHON_BASE_URL = 'http://localhost:5009'

export const FRONTEND_HOME_URL = isStaging
    ? 'https://staging.commzone.app'
    : isProduction
      ? 'https://commzone.app'
      : 'http://localhost:3000'

export const FRONTEND_APP_URL = isStaging
    ? 'https://stagingapp.commzone.app'
    : isProduction
      ? 'https://app.commzone.app'
      : 'http://localhost:3000'

export { NODE_BASE_URL }

let isRefreshing = false
let failedQueue: any[] = []
let hasRedirectedForAuth = false

const processQueue = (error: any) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve()
        }
    })
    failedQueue = []
}

const redirectToLoginOnce = () => {
    invalidateBrowserAuthState()

    if (hasRedirectedForAuth || window.location.pathname === '/login') return

    hasRedirectedForAuth = true
    window.location.href = '/login'
}

const baseRequestHeaders: { [key: string]: string } = {
    // Don't set default Content-Type - let browser/axios set it appropriately for each request type
}

const pythonRequestHeaders: { [key: string]: string } = {
    ...baseRequestHeaders,
}

export const NodeAxios: AxiosInstance = axios.create({
    baseURL: NODE_BASE_URL,
    headers: baseRequestHeaders,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT, // 4 minutes - standardized for AI generation tasks
})

export const PythonAxios: AxiosInstance = axios.create({
    baseURL: PYTHON_BASE_URL,
    headers: pythonRequestHeaders,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT, // 4 minutes - standardized for AI generation tasks
})

const AxiosInstances: AxiosInstance[] = [NodeAxios, PythonAxios]

const authToken = Cookies.get('auth')
if (authToken && authToken !== 'null') {
    pythonRequestHeaders.authorization = `Bearer ${authToken}`
}

    AxiosInstances.forEach(instance => {
    // Add a request interceptor
    instance.interceptors.request.use(
        function (config: InternalAxiosRequestConfig) {
            // Add correlation ID for tracing
            const correlationId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            config.headers['x-correlation-id'] = correlationId

            // Debug logging for upload requests
            if (config.url?.includes('upload-media')) {
                console.log('🌐 Axios interceptor - Upload request:', {
                    url: config.url,
                    method: config.method,
                    baseURL: config.baseURL,
                    headers: config.headers,
                    data: config.data instanceof FormData ? 'FormData' : config.data,
                })

                // Log FormData contents if possible
                if (config.data instanceof FormData) {
                    const formData = config.data as FormData
                    console.log('📋 FormData contents:')
                    for (const [key, value] of formData.entries()) {
                        console.log(`  ${key}:`, value instanceof File ? `File(${value.name})` : value)
                    }
                }
            }

            if (config.baseURL === PYTHON_BASE_URL) {
                const authToken = Cookies.get('auth')
                if (config.headers === undefined) {
                    config.headers = {} as AxiosHeaders
                }
                config.headers.authorization = `Bearer ${authToken}`
            }
            return config
        },
        function (error) {
            return Promise.reject(error)
        }
    )

    // Add a response interceptor
    instance.interceptors.response.use(
        function (response) {
            // Debug logging for upload responses
            if (response.config?.url?.includes('upload-media')) {
                console.log('✅ Axios response - Upload success:', {
                    url: response.config.url,
                    status: response.status,
                    data: response.data,
                })
            }
            return response.data
        },
        async function (error) {
            // Debug logging for upload errors
            if (error.config?.url?.includes('upload-media')) {
                console.log('❌ Axios response - Upload error:', {
                    url: error.config?.url,
                    status: error.response?.status,
                    message: error.message,
                    data: error.response?.data,
                    headers: error.response?.headers,
                })
            }
            // If the error status is 401, redirect to login
            if (error.response?.status === 401) {
                // Public client-portal endpoints have their own password/session flow.
                // Do not trigger app auth refresh/redirect for these routes.
                if (isPublicAuthException(error.config?.url)) {
                    return Promise.reject(error)
                }

                // Don't redirect for /me requests to prevent infinite loops
                if (!isAuthEndpoint(error.config?.url)) {
                    const originalRequest = error.config
                    if (!originalRequest._retry) {
                        if (isRefreshing) {
                            return new Promise((resolve, reject) => {
                                failedQueue.push({ resolve, reject })
                            })
                                .then(() => instance(originalRequest))
                                .catch(err => Promise.reject(err))
                        }

                        originalRequest._retry = true
                        isRefreshing = true

                        return new Promise((resolve, reject) => {
                            NodeAxios.get('/me')
                                .then(() => {
                                    processQueue(null)
                                    resolve(instance(originalRequest))
                                })
                                .catch(err => {
                                    processQueue(err)
                                    redirectToLoginOnce()
                                    reject(err)
                                })
                                .finally(() => {
                                    isRefreshing = false
                                })
                        })
                    }
                } else {
                    redirectToLoginOnce()
                    return Promise.reject(error)
                }
            }

            // Handle other errors (optional per-request opt-out for background/debug probes)
            if (!(error.config as { skipErrorToast?: boolean })?.skipErrorToast) {
                handleResponseError(error.response)
            }
            return Promise.reject(error)
        }
    )
})
// Test change to reproduce FastDeploy bug - Thu Sep 11 01:48:07 CDT 2025
// COMMIT 2: Before FastDeploy - Thu Sep 11 01:49:05 CDT 2025
// COMMIT 3: Final test commit - Thu Sep 11 01:49:20 CDT 2025
